<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('clients.title') }}</h1>
          <div class="d-flex align-center">
            <v-text-field
              v-model="clientFilter"
              :label="$t('clients.filter')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              hide-details
              class="mr-3"
              style="max-width: 320px; min-width: 220px;"
              clearable
            />
            <v-btn color="primary" @click="openAddDialog">{{ $t('clients.add') }}</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('clients.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('clients.id') }}</th>
                  <th>{{ $t('clients.phone') }}</th>
                  <th>{{ $t('clients.type') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="client in filteredClients" :key="client.id">
                  <td>{{ client.id }}</td>
                  <td><strong>{{ client.phone }}</strong></td>
                  <td>
                    <v-chip v-if="client.id === INTERNAL_CLIENT_ID" color="secondary" size="small">
                      {{ $t('clients.internal') }}
                    </v-chip>
                    <span v-else class="text-grey">{{ $t('clients.regular') }}</span>
                  </td>
                  <td>
                    <v-btn
                      v-if="client.id !== INTERNAL_CLIENT_ID"
                      variant="plain"
                      size="small"
                      @click="editClient(client)"
                      icon="mdi-pencil"
                    />
                    <v-btn
                      v-if="client.id !== INTERNAL_CLIENT_ID"
                      variant="plain"
                      size="small"
                      color="error"
                      @click="deleteClient(client.id)"
                      icon="mdi-delete"
                    />
                  </td>
                </tr>
                <tr v-if="filteredClients.length === 0">
                  <td colspan="4" class="text-center text-grey">{{ $t('clients.noClientsFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ clientForm.id ? $t('clients.edit') : $t('clients.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="clientForm.phone"
              :label="$t('clients.phone')"
              required
              density="compact"
              :rules="[v => !!v || $t('clients.phoneRequired')]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveClient">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

const INTERNAL_CLIENT_ID = 1

interface Client {
  id: number
  phone: string
}

const clients = ref<Client[]>([])
const clientFilter = ref('')
const showAddDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const clientForm = ref({
  id: null as number | null,
  phone: ''
})

const digitsOnly = (str: string) => str.replace(/\D/g, '')

const filteredClients = computed(() => {
  const query = clientFilter.value.trim().toLowerCase()
  if (!query) return clients.value
  return clients.value.filter(client => {
    const phone = client.phone.toLowerCase()
    const idMatch = String(client.id).includes(query)
    return phone.includes(query) || idMatch
  })
})

const loadClients = async () => {
  try {
    const response = await apiGet('/api/clients')
    if (response.ok) {
      clients.value = await response.json()
    }
  } catch (error) {
    console.error('Load clients error:', error)
  }
}

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editClient = (client: Client) => {
  clientForm.value = {
    id: client.id,
    phone: client.phone
  }
  showAddDialog.value = true
}

const resetForm = () => {
  clientForm.value = {
    id: null,
    phone: ''
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveClient = async () => {
  const phone = clientForm.value.phone.trim()
  if (!phone) {
    showError(t('clients.fillPhone'))
    return
  }
  if (digitsOnly(phone).length < 10) {
    showError(t('transactions.enterValidPhone'))
    return
  }

  try {
    const url = clientForm.value.id ? `/api/clients/${clientForm.value.id}` : '/api/clients'
    const response = clientForm.value.id
      ? await apiPut(url, { client: { phone } })
      : await apiPost(url, { client: { phone } })

    if (response.ok) {
      await loadClients()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('clients.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save client error:', error)
    const errorText = error instanceof Error ? error.message : t('errors.unknownError')
    showError(t('clients.errorSaving', { errors: errorText }))
  }
}

const deleteClient = async (id: number) => {
  if (id === INTERNAL_CLIENT_ID) return
  if (!confirm(t('clients.areYouSureDelete'))) return
  try {
    const response = await apiDelete(`/api/clients/${id}`)
    if (response.ok) {
      await loadClients()
    } else {
      showError(t('clients.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete client error:', error)
    showError(t('clients.errorDeleting'))
  }
}

onMounted(() => {
  loadClients()
})
</script>
